import { useRef, useState } from "react";
import {
  Avatar,
  Popover,
  ReadTicks,
  QUICK_REACTIONS,
  clockTime,
  isImageUrl,
  isAudioUrl,
  isVideoUrl,
  isFileUrl,
  attachmentParts,
  splitPreviewGlyph,
  PreviewIcon,
  fileMeta,
  emojiOnlyCount,
  linkify,
  youtubeId,
  sentBubble,
  ReplyIcon,
  ForwardIcon,
  CopyIcon,
  EditIcon,
  TrashIcon,
  InfoIcon,
  SmileIcon,
  DotsIcon,
  DocIcon,
  DownloadIcon,
  CheckIcon,
  PlusIcon,
} from "./ui";
import YouTubeCard from "./YouTubeCard";
import EmojiPicker from "../EmojiPicker";

const SelectIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 12.2l2.4 2.4 4.6-5" />
  </svg>
);

/** Minimal voice-note player — play/pause + a scrub bar + duration. */
function VoiceNote({ url, mine, primary }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };
  const fmt = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };

  return (
    <div className="flex min-w-[190px] items-center gap-2.5">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition"
        style={mine ? { backgroundColor: "rgba(255,255,255,0.22)", color: "rgb(var(--c-surface))" } : { backgroundColor: `${primary}18`, color: primary }}
      >
        {playing ? (
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><rect x="7" y="5" width="3.5" height="14" rx="1" /><rect x="13.5" y="5" width="3.5" height="14" rx="1" /></svg>
        ) : (
          <svg className="h-4 w-4 translate-x-[1px]" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5.5l12 6.5-12 6.5v-13z" /></svg>
        )}
      </button>
      <div className="min-w-0 flex-1">
        <input
          type="range"
          min={0}
          max={dur || 0}
          step="0.1"
          value={pos}
          onChange={(e) => {
            const v = Number(e.target.value);
            setPos(v);
            if (audioRef.current) audioRef.current.currentTime = v;
          }}
          className="chat-range w-full"
          style={{ accentColor: mine ? "#fff" : primary }}
        />
        <span className={`mt-0.5 block text-[10px] font-semibold tabular-nums ${mine ? "text-white/75" : "text-slate-400"}`}>
          {fmt(pos)} / {fmt(dur)}
        </span>
      </div>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onEnded={() => {
          setPlaying(false);
          setPos(0);
        }}
        className="hidden"
      />
    </div>
  );
}

/** WhatsApp-style video: poster frame + centered play button + duration badge;
 *  taps play inline with native controls. */
function VideoBubble({ url }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);

  const play = () => {
    const v = ref.current;
    if (!v) return;
    v.play();
    setPlaying(true);
  };
  const fmt = (s) => {
    if (!Number.isFinite(s) || s <= 0) return "";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-black">
      <video
        ref={ref}
        src={url}
        preload="metadata"
        controls={playing}
        playsInline
        onLoadedMetadata={(e) => {
          setDur(e.currentTarget.duration);
          // Nudge to the first frame so the poster isn't a black rectangle.
          try {
            if (e.currentTarget.currentTime === 0) e.currentTarget.currentTime = 0.1;
          } catch {
            /* ignore */
          }
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        className="max-h-72 w-full max-w-[320px] object-cover"
      />
      {!playing && (
        <button
          type="button"
          onClick={play}
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center bg-black/15 transition hover:bg-black/25"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 shadow-lg backdrop-blur-sm">
            <svg className="h-6 w-6 translate-x-[1px] text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5.5l12 6.5-12 6.5z" /></svg>
          </span>
        </button>
      )}
      {!playing && dur > 0 && (
        <span className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white backdrop-blur-sm">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="6" width="13" height="12" rx="2" /><path d="M16 10l5-3v10l-5-3" /></svg>
          {fmt(dur)}
        </span>
      )}
    </div>
  );
}

/**
 * One message: bubble, attachment renderer, quoted reply, reactions, the hover
 * action rail and the long-press / caret menu.
 *
 * Layout rules (Instagram/WhatsApp hybrid):
 *  - avatars only on the received side, once per group
 *  - time + ticks sit INSIDE the bubble, bottom-right, via a reserved spacer
 *  - reactions overlap the bubble's bottom edge
 *  - emoji-only messages drop the bubble entirely
 */
export default function MessageRow({
  m,
  firstOfGroup,
  lastOfGroup,
  primary,
  myId,
  otherName,
  otherImg,
  menuOpen,
  onMenu,
  selectMode,
  selected,
  onToggleSelect,
  onReact,
  onReply,
  onEdit,
  onDelete,
  onCopy,
  onForward,
  onSelectMode,
  onInfo,
  onOpenImage,
  onJumpTo,
  onRetry,
}) {
  const mine = !!m.is_me;
  const pending = m.status === "sending";
  const failed = m.status === "failed";
  const body = m.body || "";
  // An attachment may carry a caption ("<url>\n<caption>"). `mediaBody` is the
  // URL we render the photo/video/file from; `attCaption` is the text below it.
  const { url: attUrl, caption: attCaption } = m.deleted ? { url: null, caption: "" } : attachmentParts(body);
  const mediaBody = attUrl || body;
  const img = !m.deleted && isImageUrl(mediaBody);
  const audio = !m.deleted && isAudioUrl(mediaBody);
  const video = !m.deleted && isVideoUrl(mediaBody);
  const file = !m.deleted && isFileUrl(mediaBody);
  const jumbo = !m.deleted && !img && !audio && !video && !file ? emojiOnlyCount(body) : 0;
  // A YouTube link in a plain-text message → rich preview card (WhatsApp-style).
  const ytId = !m.deleted && !img && !audio && !video && !file && !jumbo ? youtubeId(body) : null;
  // Caption = the message text with the YouTube URL stripped out (so we never
  // show a raw URL dangling under the preview card).
  const caption = ytId ? body.replace(/https?:\/\/[^\s]+/g, "").trim() : "";
  // A photo/video with a caption can't overlay its meta on the image — it needs
  // the caption + inline meta below, like a text bubble.
  const mediaCaptioned = (img || video) && !m.deleted && !m.reply_to && !!attCaption;
  // Emoji-only messages drop the bubble — unless they quote something, which
  // still needs the bubble to hold the quote card.
  const bare = jumbo > 0 && !m.reply_to;
  // CONSISTENT bubbles: every message lives in ONE bubble — brand-tinted for the
  // sender, white for the receiver. Photos/videos fill the bubble with a thin
  // frame; documents & link previews are cards inside it. `isMedia` = thin-frame
  // fill, `isCardBubble` = card + inline meta.
  const isMedia = (img || video) && !m.deleted && !m.reply_to && !attCaption;
  const isCardBubble = (file || !!ytId) && !m.deleted;
  const tintBubble = mine && !m.deleted; // ALL sent messages share the brand bubble
  const hasReactions = m.reactions && m.reactions.length > 0;
  const [reactBar, setReactBar] = useState(false); // quick-reaction popover
  // Anchors for the portalled menus (see Popover — they must escape the
  // thread canvas, which clips anything that outgrows it).
  const bubbleRef = useRef(null);
  const reactBtnRef = useRef(null);

  // ---- swipe-to-reply (mobile) -----------------------------------------
  const touch = useRef({ x: 0, y: 0, active: false });
  const [slide, setSlide] = useState(0);
  const longPress = useRef(null);

  const onTouchStart = (e) => {
    if (m.deleted) return;
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, active: true };
    longPress.current = setTimeout(() => {
      touch.current.active = false;
      onMenu(m.id);
    }, 480);
  };
  const onTouchMove = (e) => {
    if (!touch.current.active) return;
    const t = e.touches[0];
    const dx = t.clientX - touch.current.x;
    const dy = Math.abs(t.clientY - touch.current.y);
    // 18px of vertical travel was enough to start a scroll AND still fire the
    // long-press menu 480ms later; 10 cancels it as soon as the list moves.
    if (dy > 10) {
      clearTimeout(longPress.current);
      touch.current.active = false;
      setSlide(0);
      return;
    }
    if (dx > 6) {
      clearTimeout(longPress.current);
      setSlide(Math.min(dx, 72));
    }
  };
  const onTouchEnd = () => {
    clearTimeout(longPress.current);
    if (slide > 48 && !m.deleted) onReply(m);
    setSlide(0);
    touch.current.active = false;
  };

  const bubbleStyle = m.deleted
    ? { backgroundColor: "rgb(var(--c-surface-sunken))", color: "rgb(var(--c-slate-400))" }
    : tintBubble
      ? { ...sentBubble(primary), color: "rgb(var(--c-slate-900))" } // dark text on the light-brand bubble
      : undefined;

  // Reserved space so the floating time/ticks never overlap the last text line.
  // The old fixed 38/58px was narrower than a 12-hour clock ("12:43 PM"), so the
  // time printed straight over the last word. Measure off the actual string at
  // the meta's 10px tabular font instead: ~5.6px a glyph, +22 for the tick pair,
  // +34 for the "edited" prefix, +6 of breathing room.
  const timeStr = clockTime(m.created_at);
  const metaSpacer = Math.round(timeStr.length * 5.6) + (m.edited ? 34 : 0) + (mine ? 22 : 0) + 6;

  // Meta rendered as its own row (used by document / link-preview cards, which
  // can't reserve inline space for the floating variant).
  const metaInline = !m.deleted && (
    <span className="mt-1 flex items-center gap-1 self-end text-[10px] font-medium tabular-nums text-slate-400">
      {m.edited && <span className="opacity-80">edited</span>}
      <span>{clockTime(m.created_at)}</span>
      {mine &&
        (pending ? (
          <svg className="h-3 w-3 animate-spin opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="9" strokeOpacity="0.3" /><path strokeLinecap="round" d="M21 12a9 9 0 00-9-9" /></svg>
        ) : (
          <ReadTicks read={m.read} delivered={m.delivered} className="h-3 w-[16px]" />
        ))}
    </span>
  );

  const meta = (over) => (
    <span
      className={`pointer-events-none flex items-center gap-1 text-[10px] font-medium tabular-nums ${
        over
          ? "absolute bottom-1.5 right-2 rounded-full bg-black/45 px-1.5 py-0.5 text-white backdrop-blur-sm"
          : "absolute bottom-1 right-2.5 text-slate-400"
      }`}
    >
      {m.edited && !m.deleted && <span className="opacity-80">edited</span>}
      <span>{clockTime(m.created_at)}</span>
      {mine && !m.deleted && (pending ? (
        <svg className="h-3 w-3 animate-spin opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="9" strokeOpacity="0.3" /><path strokeLinecap="round" d="M21 12a9 9 0 00-9-9" />
        </svg>
      ) : (
        <ReadTicks read={m.read} delivered={m.delivered} className="h-3 w-[16px]" />
      ))}
    </span>
  );

  const quoted = m.reply_to && (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onJumpTo(m.reply_to.id);
      }}
      className="mb-1.5 block w-full overflow-hidden rounded-lg border-l-[3px] bg-black/[0.05] px-2 py-1 text-left transition hover:bg-black/[0.08]"
      style={{ borderColor: primary }}
    >
      <span className="block text-[11px] font-bold" style={{ color: primary }}>
        {m.reply_to.sender_id === myId ? "You" : otherName}
      </span>
      {(() => {
        if (m.reply_to.deleted) return <span className="block truncate text-[11px] text-slate-500">Message deleted</span>;
        const { kind, text } = splitPreviewGlyph(m.reply_to.preview);
        return (
          <span className="flex items-center gap-1 truncate text-[11px] text-slate-500">
            <PreviewIcon kind={kind} className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate">{text}</span>
          </span>
        );
      })()}
    </button>
  );

  const content = m.deleted ? (
    <span className="flex items-center gap-1.5 pr-10 text-[13px] italic text-slate-400">
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M6.5 6.5l11 11" /></svg>
      This message was deleted
    </span>
  ) : img ? (
    <div className="flex flex-col">
      <button type="button" onClick={() => onOpenImage(mediaBody)} className="relative block overflow-hidden rounded-2xl">
        <img src={mediaBody} alt="" loading="lazy" className="block max-h-72 w-auto max-w-full rounded-2xl object-cover" />
      </button>
      {attCaption && (
        <span className="mt-1.5 whitespace-pre-wrap break-words px-1.5 text-[13.5px] leading-snug [overflow-wrap:anywhere]">
          {linkify(attCaption, "underline underline-offset-2")}
        </span>
      )}
      {mediaCaptioned && <span className="px-1.5">{metaInline}</span>}
    </div>
  ) : video ? (
    <div className="flex flex-col">
      <VideoBubble url={mediaBody} />
      {attCaption && (
        <span className="mt-1.5 whitespace-pre-wrap break-words px-1.5 text-[13.5px] leading-snug [overflow-wrap:anywhere]">
          {linkify(attCaption, "underline underline-offset-2")}
        </span>
      )}
      {mediaCaptioned && <span className="px-1.5">{metaInline}</span>}
    </div>
  ) : audio ? (
    <VoiceNote url={mediaBody} mine={false} primary={primary} />
  ) : file ? (
    (() => {
      const fm = fileMeta(mediaBody);
      // Card adapts to the bubble colour: light-on-brand when sent, slate when received.
      return (
        <div className="flex flex-col">
          <a
            href={mediaBody}
            target="_blank"
            rel="noreferrer"
            download
            className="flex min-w-[210px] max-w-[280px] items-center gap-3 rounded-xl bg-surface/70 px-2.5 py-2 ring-1 ring-black/[0.04] transition hover:bg-surface"
          >
            <span className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl text-white shadow-sm" style={{ backgroundColor: fm.color }}>
              <DocIcon className="h-[18px] w-[18px]" />
              <span className="mt-0.5 text-[7px] font-extrabold leading-none tracking-wide">{fm.label}</span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-bold text-slate-800">{fm.name}</span>
              <span className="block text-[10px] font-semibold text-slate-400">{fm.label} · Tap to open</span>
            </span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface text-slate-500 ring-1 ring-slate-200/70">
              <DownloadIcon className="h-4 w-4" />
            </span>
          </a>
          {attCaption && (
            <span className="mt-1.5 whitespace-pre-wrap break-words px-0.5 text-[13.5px] leading-snug [overflow-wrap:anywhere]">
              {linkify(attCaption, "underline underline-offset-2")}
            </span>
          )}
          {metaInline}
        </div>
      );
    })()
  ) : ytId ? (
    // Unified link preview: ONE card (thumbnail + title + channel), the optional
    // caption, then meta — all inside the same bubble. No raw URL dangling below.
    <div className="flex w-[260px] max-w-full flex-col">
      <YouTubeCard url={`https://www.youtube.com/watch?v=${ytId}`} videoId={ytId} mine={false} />
      {caption && (
        <span className="mt-1.5 block whitespace-pre-wrap text-[13px] leading-snug text-slate-700 [overflow-wrap:anywhere]">
          {linkify(caption, "underline underline-offset-2")}
        </span>
      )}
      {metaInline}
    </div>
  ) : (
    <span className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
      {linkify(body, "underline underline-offset-2")}
      <span className="inline-block h-1 align-baseline" style={{ width: metaSpacer }} />
    </span>
  );

  // ---- hover rail (desktop) — premium floating toolbar -----------------
  const railBtn = "flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:text-slate-700";
  const rail = !m.deleted && !selectMode && (
    <div
      className={`absolute top-1/2 z-[5] hidden -translate-y-1/2 items-center gap-0.5 rounded-full bg-surface/95 p-1 opacity-0 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)] ring-1 ring-slate-900/[0.06] backdrop-blur transition group-hover:opacity-100 sm:flex ${
        reactBar ? "!opacity-100" : ""
      } ${mine ? "right-full mr-2" : "left-full ml-2"}`}
    >
      <button ref={reactBtnRef} type="button" onClick={() => setReactBar((o) => !o)} title="React" className={`${railBtn} hover:bg-amber-50 dark:hover:bg-amber-500/15 hover:text-amber-500 ${reactBar ? "bg-amber-50 dark:bg-amber-500/15 text-amber-500" : ""}`}>
        <SmileIcon className="h-[18px] w-[18px]" />
      </button>
      <button type="button" onClick={() => onReply(m)} title="Reply" className={`${railBtn} hover:bg-slate-100`}>
        <ReplyIcon className="h-[18px] w-[18px]" />
      </button>
      {/* Toggling explicitly: the menu's anchor is the bubble, which contains
          this button, so the outside-click close deliberately skips it. */}
      <button type="button" onClick={() => onMenu(menuOpen ? null : m.id)} title="More" className={`${railBtn} hover:bg-slate-100`}>
        <DotsIcon className="h-[18px] w-[18px]" />
      </button>

      {/* Quick-reaction bar: 6 emojis + a "+" that opens the full picker.
          Not scrollable — it holds the emoji picker's own flyout. */}
      <Popover
        open={reactBar}
        onClose={() => setReactBar(false)}
        anchorRef={reactBtnRef}
        align={mine ? "end" : "start"}
        scrollable={false}
        className="!rounded-full !p-1"
      >
        <div className="flex items-center gap-0.5">
          {QUICK_REACTIONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                onReact(m, e);
                setReactBar(false);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full text-[20px] leading-none transition hover:-translate-y-0.5 hover:scale-110 hover:bg-slate-100"
            >
              {e}
            </button>
          ))}
          <span className="mx-0.5 h-6 w-px bg-slate-200" />
          <EmojiPicker
            dropUp
            icon={<PlusIcon className="h-[18px] w-[18px]" width="2.2" />}
            onPick={(e) => {
              onReact(m, e);
              setReactBar(false);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
          />
        </div>
      </Popover>
    </div>
  );

  const menuItem = (Icon, label, fn, danger) => (
    <button
      type="button"
      onClick={() => {
        onMenu(null);
        fn();
      }}
      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition ${
        danger ? "text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/15" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon className={`h-4 w-4 ${danger ? "" : "text-slate-400"}`} />
      {label}
    </button>
  );

  return (
    <div
      id={`chatmsg-${m.id}`}
      /* In select mode the WHOLE row is the hit target. The 20px checkbox used
         to be the only thing that toggled selection, so tapping the message —
         which is what everyone tries — did nothing at all. */
      className={`group relative flex items-end gap-2 px-1 ${mine ? "flex-row-reverse" : ""} ${firstOfGroup ? "mt-4" : "mt-1.5"} ${
        selectMode ? "cursor-pointer rounded-xl py-1 transition" : ""
      }`}
      /* Capture phase: in select mode a tap must SELECT, never also open the
         attachment, follow the link or fire the bubble's own handlers. */
      onClickCapture={selectMode ? (e) => { e.preventDefault(); e.stopPropagation(); onToggleSelect(m.id); } : undefined}
      onDoubleClick={() => !m.deleted && !selectMode && onReact(m, "❤️")}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      /* One style object: the swipe-to-reply transform and the selected tint
         both live here. Two `style` props would mean the second silently wins. */
      style={{
        ...(slide ? { transform: `translateX(${slide}px)`, transition: "none" } : { transition: "transform .18s ease" }),
        ...(selectMode && selected ? { backgroundColor: `${primary}14` } : null),
      }}
    >
      {/* select-mode checkbox */}
      {selectMode && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(m.id); }}
          aria-label={selected ? "Deselect" : "Select"}
          className={`flex h-5 w-5 shrink-0 items-center justify-center self-center rounded-full border-2 transition ${
            selected ? "border-transparent text-white" : "border-slate-300 bg-surface text-transparent"
          }`}
          style={selected ? { backgroundColor: primary } : undefined}
        >
          <CheckIcon className="h-3 w-3" width="3" />
        </button>
      )}

      {/* avatar — received side only, once per group */}
      {!mine &&
        (lastOfGroup ? (
          <Avatar name={otherName} img={otherImg} primary={primary} size={28} />
        ) : (
          <span className="w-[28px] shrink-0" />
        ))}

      <div className={`relative flex min-w-0 max-w-[86%] flex-col sm:max-w-[70%] ${mine ? "items-end" : "items-start"}`}>
        <div ref={bubbleRef} className="relative">
          {rail}

          {bare ? (
            <div className={`relative pb-4 ${mine ? "text-right" : ""}`}>
              <span className="block leading-none" style={{ fontSize: jumbo === 1 ? 46 : jumbo === 2 ? 38 : 32 }}>
                {body}
              </span>
              <span className={`absolute bottom-0 flex items-center gap-1 text-[10px] text-slate-400 ${mine ? "right-0" : "left-0"}`}>
                <span>{clockTime(m.created_at)}</span>
                {mine && <ReadTicks read={m.read} delivered={m.delivered} className="h-3 w-[16px]" />}
              </span>
            </div>
          ) : (
            /* ONE consistent bubble for everything: brand-tinted when sent, white
               when received. Media fills with a thin frame; cards sit inside. */
            <div
              className={`chat-bubble relative overflow-hidden text-[13.5px] leading-[1.45] ${
                isMedia || mediaCaptioned ? "p-1" : isCardBubble ? "p-1.5" : audio ? "px-3.5 py-2 pb-5" : "px-3.5 py-2"
              } ${lastOfGroup && !isMedia && !mediaCaptioned ? (tintBubble ? "chat-tail-r" : "chat-tail-l") : ""} ${
                tintBubble
                  ? "rounded-[20px]"
                  : "rounded-[20px] bg-surface text-slate-800 shadow-[0_2px_8px_-3px_rgba(16,24,40,0.12),0_1px_2px_rgba(16,24,40,0.05)] ring-1 ring-slate-100"
              } ${pending ? "opacity-70" : ""} ${failed ? "ring-2 ring-rose-300 dark:ring-rose-500/30" : ""}`}
              style={bubbleStyle}
            >
              {quoted}
              {content}
              {/* Bare media overlays its meta; captioned media & cards render
                  their own inline meta inside `content`; text keeps the floating
                  variant. */}
              {isMedia ? meta(true) : isCardBubble || mediaCaptioned ? null : meta(false)}
            </div>
          )}

          {/* reactions — a clean WHITE pill on the trailing bottom corner
              (right for sent, left for received). No hover motion. */}
          {hasReactions && (
            <div className={`absolute -bottom-2.5 z-[2] flex flex-wrap gap-1 ${mine ? "right-2" : "left-2"}`}>
              {m.reactions.map((r) => (
                <button
                  key={r.emoji}
                  type="button"
                  onClick={() => onReact(m, r.emoji)}
                  className="flex items-center gap-1 rounded-full bg-surface px-2 py-[3px] text-[13px] leading-none shadow-[0_3px_10px_-2px_rgba(15,23,42,0.28)] ring-1 ring-slate-200/80"
                >
                  <span className="leading-none">{r.emoji}</span>
                  {r.count > 1 && <span className="text-[11px] font-bold text-slate-500">{r.count}</span>}
                </button>
              ))}
            </div>
          )}

          {/* message menu — portalled off the bubble so the thread canvas can't
              clip it (it used to lose its top half against the header). */}
          <Popover
            open={menuOpen}
            onClose={() => onMenu(null)}
            anchorRef={bubbleRef}
            align={mine ? "end" : "start"}
            width={212}
          >
            <div className="mb-1 flex items-center justify-between gap-0.5 rounded-xl bg-slate-50 px-1 py-1">
              {QUICK_REACTIONS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => {
                    onMenu(null);
                    onReact(m, e);
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-lg leading-none transition hover:scale-125 hover:bg-surface hover:shadow-sm"
                >
                  {e}
                </button>
              ))}
            </div>
            {menuItem(ReplyIcon, "Reply", () => onReply(m))}
            {menuItem(ForwardIcon, "Forward", () => onForward(m))}
            {menuItem(SelectIcon, "Select messages", () => onSelectMode(m.id))}
            {!attUrl && !isImageUrl(body) && !isFileUrl(body) && menuItem(CopyIcon, "Copy text", () => onCopy(m))}
            {mine && !attUrl && !isImageUrl(body) && !isFileUrl(body) && menuItem(EditIcon, "Edit", () => onEdit(m))}
            {menuItem(InfoIcon, "Info", () => onInfo(m))}
            {mine && menuItem(TrashIcon, "Delete", () => onDelete(m.id), true)}
          </Popover>
        </div>

        {/* failed send → retry */}
        {failed && (
          <button
            type="button"
            onClick={() => onRetry(m)}
            className="mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500 hover:underline"
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 9a8 8 0 0113.7-4.7L20 7M20 4v3.5h-3.5M20 15a8 8 0 01-13.7 4.7L4 17m0 3v-3.5h3.5" /></svg>
            Not sent · Retry
          </button>
        )}

        {hasReactions && <span className="h-3.5" />}
      </div>
    </div>
  );
}
